import { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface TimerContextType {
  timerSeconds: number | null;
  isPaused: boolean;
  startTimer: (seconds: number) => void;
  stopTimer: () => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  extendTimer: (additionalSeconds: number) => void;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export function TimerProvider({ children }: { children: ReactNode }) {
  const [timerSeconds, setTimerSeconds] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [pausedRemaining, setPausedRemaining] = useState<number | null>(null);

  // Load timer from localStorage on mount
  useEffect(() => {
    const loadTimerState = () => {
      const savedTimer = localStorage.getItem("turnTimer");
      const savedPause = localStorage.getItem("turnTimerPaused");
      
      if (savedTimer) {
        try {
          const timerData = JSON.parse(savedTimer);
          // Support both old format (endTime) and new format (server_end_time)
          if (timerData.server_end_time && typeof timerData.time_offset === "number") {
            // New format: calculate based on server time
            const current_server_time = Date.now() - timerData.time_offset;
            const remaining = Math.max(0, Math.floor((timerData.server_end_time - current_server_time) / 1000));
            setTimerSeconds(remaining);
          } else if (timerData.endTime) {
            // Old format: backward compatibility
            const remaining = Math.max(0, Math.floor((timerData.endTime - Date.now()) / 1000));
            setTimerSeconds(remaining);
          } else {
            setTimerSeconds(0);
          }
        } catch (e) {
          console.error("Error parsing turnTimer from localStorage:", e);
          setTimerSeconds(0);
        }
      } else {
        // No timer in localStorage - set to 0 to show timer (but expired/not started)
        setTimerSeconds(0);
      }
      
      if (savedPause) {
        try {
          const pauseData = JSON.parse(savedPause);
          if (pauseData.paused && pauseData.remaining !== null && pauseData.remaining !== undefined) {
            setIsPaused(true);
            setPausedRemaining(pauseData.remaining);
            setTimerSeconds(pauseData.remaining);
          } else {
            // If pause state is cleared, ensure isPaused is false
            setIsPaused(false);
            setPausedRemaining(null);
          }
        } catch (e) {
          console.error("Error parsing turnTimerPaused from localStorage:", e);
          setIsPaused(false);
          setPausedRemaining(null);
        }
      } else {
        // If no pause state in localStorage, ensure isPaused is false
        setIsPaused(false);
        setPausedRemaining(null);
      }
    };

    loadTimerState();

    // Listen for storage changes from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "turnTimer" || e.key === "turnTimerPaused") {
        loadTimerState();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Listen for timer updates from WebSocket (when admin starts/stops/pauses/resumes timer)
    const handleTimerUpdate = (e: CustomEvent) => {
      const { timer_seconds, action, server_time, end_time, is_paused } = e.detail;
      console.log("Timer update event received:", { timer_seconds, action, server_time, end_time, is_paused });
      
      const client_received_time = Date.now();
      
      if (action === "start" && typeof timer_seconds === "number" && typeof end_time === "number") {
        // Start timer using server time for synchronization
        // Store server end_time and calculate time offset
        const time_offset = client_received_time - server_time; // Client clock - Server clock
        const server_end_time = end_time;
        
        localStorage.setItem("turnTimer", JSON.stringify({ 
          server_end_time,
          server_time,
          client_received_time,
          time_offset
        }));
        localStorage.removeItem("turnTimerPaused");
        setTimerSeconds(timer_seconds);
        setIsPaused(false);
        setPausedRemaining(null);
      } else if (action === "stop") {
        // Stop timer
        localStorage.removeItem("turnTimer");
        localStorage.removeItem("turnTimerPaused");
        setTimerSeconds(0);
        setIsPaused(false);
        setPausedRemaining(null);
      } else if (action === "pause") {
        // Pause timer - save current remaining time based on server time
        const savedTimer = localStorage.getItem("turnTimer");
        if (savedTimer) {
          try {
            const timerData = JSON.parse(savedTimer);
            if (timerData.server_end_time && timerData.time_offset) {
              // Calculate remaining time based on server time
              const current_server_time = Date.now() - timerData.time_offset;
              const remaining = Math.max(0, Math.floor((timerData.server_end_time - current_server_time) / 1000));
              setPausedRemaining(remaining);
              setIsPaused(true);
              localStorage.setItem("turnTimerPaused", JSON.stringify({ 
                paused: true, 
                remaining,
                server_end_time: timerData.server_end_time,
                time_offset: timerData.time_offset
              }));
            }
          } catch (e) {
            console.error("Error parsing turnTimer for pause:", e);
          }
        }
        // Also handle is_paused from event if provided (for admin dashboard)
        if (is_paused !== undefined) {
          setIsPaused(is_paused);
        }
      } else if (action === "resume" && typeof server_time === "number") {
        // Resume timer - restore from paused state using server time
        const savedPause = localStorage.getItem("turnTimerPaused");
        if (savedPause) {
          try {
            const pauseData = JSON.parse(savedPause);
            if (typeof pauseData.remaining === "number" && pauseData.remaining > 0) {
              // Calculate new server end_time based on remaining time and current server time
              const time_offset = client_received_time - server_time;
              const server_end_time = server_time + (pauseData.remaining * 1000);
              
              localStorage.setItem("turnTimer", JSON.stringify({ 
                server_end_time,
                server_time,
                client_received_time,
                time_offset
              }));
              localStorage.removeItem("turnTimerPaused");
              setTimerSeconds(pauseData.remaining);
              setIsPaused(false);
              setPausedRemaining(null);
            }
          } catch (e) {
            console.error("Error parsing turnTimerPaused for resume:", e);
          }
        }
        // Also handle is_paused from event if provided (for admin dashboard)
        if (is_paused !== undefined) {
          setIsPaused(is_paused);
        }
      } else if (action === "extend" && typeof e.detail.additional_seconds === "number" && typeof server_time === "number") {
        // Extend timer - use new total time from server if provided, otherwise calculate locally
        const savedTimer = localStorage.getItem("turnTimer");
        const savedPause = localStorage.getItem("turnTimerPaused");
        
        // If server sent new total time and end_time, use that for synchronization
        if (typeof e.detail.timer_seconds === "number" && typeof e.detail.end_time === "number") {
          // Server calculated the new total - use it directly
          const time_offset = client_received_time - server_time;
          const server_end_time = e.detail.end_time;
          const was_paused = savedPause !== null; // Check if timer was paused before extend
          const should_remain_paused = e.detail.is_paused !== undefined ? e.detail.is_paused : was_paused;
          
          // Update the active timer
          localStorage.setItem("turnTimer", JSON.stringify({ 
            server_end_time,
            server_time,
            client_received_time,
            time_offset
          }));
          
          // Preserve pause state if timer was paused or server says it should be paused
          if (should_remain_paused) {
            // Keep timer paused with new extended time
            localStorage.setItem("turnTimerPaused", JSON.stringify({ 
              paused: true, 
              remaining: e.detail.timer_seconds,
              server_end_time: server_end_time,
              time_offset: time_offset
            }));
            setPausedRemaining(e.detail.timer_seconds);
            setIsPaused(true);
          } else {
            // Timer should be playing
            localStorage.removeItem("turnTimerPaused");
            setIsPaused(false);
            setPausedRemaining(null);
          }
          
          setTimerSeconds(e.detail.timer_seconds);
        } else {
          // Fallback: calculate locally (for backward compatibility or if server didn't send total)
          if (savedTimer) {
            try {
              const timerData = JSON.parse(savedTimer);
              if (timerData.server_end_time && typeof timerData.time_offset === "number") {
                // Calculate current remaining time
                const current_server_time = Date.now() - timerData.time_offset;
                const current_remaining = Math.max(0, Math.floor((timerData.server_end_time - current_server_time) / 1000));
                
                // Add additional seconds to the server_end_time
                const new_server_end_time = timerData.server_end_time + (e.detail.additional_seconds * 1000);
                const time_offset = client_received_time - server_time;
                
                localStorage.setItem("turnTimer", JSON.stringify({ 
                  server_end_time: new_server_end_time,
                  server_time,
                  client_received_time,
                  time_offset
                }));
                
                // Update timer seconds to show new remaining time
                const new_remaining = current_remaining + e.detail.additional_seconds;
                setTimerSeconds(new_remaining);
                
                // If paused, also update paused remaining
                if (isPaused && savedPause) {
                  try {
                    const pauseData = JSON.parse(savedPause);
                    if (typeof pauseData.remaining === "number") {
                      const new_paused_remaining = pauseData.remaining + e.detail.additional_seconds;
                      localStorage.setItem("turnTimerPaused", JSON.stringify({ 
                        paused: true, 
                        remaining: new_paused_remaining,
                        server_end_time: new_server_end_time,
                        time_offset: time_offset
                      }));
                      setPausedRemaining(new_paused_remaining);
                    }
                  } catch (e) {
                    console.error("Error updating paused state for extend:", e);
                  }
                }
              }
            } catch (e) {
              console.error("Error parsing turnTimer for extend:", e);
            }
          } else if (savedPause) {
            // Timer is paused but no active timer - extend the paused remaining
            try {
              const pauseData = JSON.parse(savedPause);
              if (typeof pauseData.remaining === "number") {
                const new_remaining = pauseData.remaining + e.detail.additional_seconds;
                const time_offset = client_received_time - server_time;
                const server_end_time = server_time + (new_remaining * 1000);
                
                localStorage.setItem("turnTimerPaused", JSON.stringify({ 
                  paused: true, 
                  remaining: new_remaining,
                  server_end_time: server_end_time,
                  time_offset: time_offset
                }));
                setPausedRemaining(new_remaining);
                setTimerSeconds(new_remaining);
              }
            } catch (e) {
              console.error("Error parsing turnTimerPaused for extend:", e);
            }
          }
        }
      }
    };

    window.addEventListener("timerUpdate", handleTimerUpdate as EventListener);

    // Also poll localStorage periodically to catch changes from same tab
    const pollInterval = setInterval(() => {
      loadTimerState();
    }, 500); // Check every 500ms

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("timerUpdate", handleTimerUpdate as EventListener);
      clearInterval(pollInterval);
    };
  }, [isPaused]);

  // Update timer every second (only when not paused)
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      const savedTimer = localStorage.getItem("turnTimer");
      if (savedTimer) {
        try {
          const timerData = JSON.parse(savedTimer);
          let remaining = 0;
          
          // Support both old format (endTime) and new format (server_end_time)
          if (timerData.server_end_time && typeof timerData.time_offset === "number") {
            // New format: calculate based on server time for synchronization
            const current_server_time = Date.now() - timerData.time_offset;
            remaining = Math.max(0, Math.floor((timerData.server_end_time - current_server_time) / 1000));
          } else if (timerData.endTime) {
            // Old format: backward compatibility
            remaining = Math.max(0, Math.floor((timerData.endTime - Date.now()) / 1000));
          }
          
          setTimerSeconds(remaining);
          // Keep timer at 0 instead of removing it - don't clear localStorage
          // Timer will stay visible showing "00:00"
        } catch (e) {
          console.error("Error parsing turnTimer in interval:", e);
        }
      } else {
        // No timer in localStorage - set to 0 to show timer (but expired/not started)
        const savedPause = localStorage.getItem("turnTimerPaused");
        if (!savedPause) {
          setTimerSeconds(0); // Show 0 instead of null
          setIsPaused(false);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [timerSeconds, isPaused]);

  const startTimer = (seconds: number) => {
    const endTime = Date.now() + seconds * 1000;
    localStorage.setItem("turnTimer", JSON.stringify({ endTime }));
    localStorage.removeItem("turnTimerPaused");
    setTimerSeconds(seconds);
    setIsPaused(false);
    setPausedRemaining(null);
  };

  const stopTimer = () => {
    localStorage.removeItem("turnTimer");
    localStorage.removeItem("turnTimerPaused");
    setTimerSeconds(null);
    setIsPaused(false);
    setPausedRemaining(null);
  };

  const pauseTimer = () => {
    if (timerSeconds === null || timerSeconds <= 0) return;
    const savedTimer = localStorage.getItem("turnTimer");
    if (savedTimer) {
      const { endTime } = JSON.parse(savedTimer);
      const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
      setPausedRemaining(remaining);
      setIsPaused(true);
      localStorage.setItem("turnTimerPaused", JSON.stringify({ paused: true, remaining }));
    }
  };

  const resumeTimer = () => {
    if (pausedRemaining === null) return;
    const endTime = Date.now() + pausedRemaining * 1000;
    localStorage.setItem("turnTimer", JSON.stringify({ endTime }));
    localStorage.removeItem("turnTimerPaused");
    setTimerSeconds(pausedRemaining);
    setIsPaused(false);
    setPausedRemaining(null);
  };

  const extendTimer = (additionalSeconds: number) => {
    const savedTimer = localStorage.getItem("turnTimer");
    const savedPause = localStorage.getItem("turnTimerPaused");
    
    if (savedTimer) {
      try {
        const timerData = JSON.parse(savedTimer);
        if (timerData.server_end_time && typeof timerData.time_offset === "number") {
          // Calculate current remaining time
          const current_server_time = Date.now() - timerData.time_offset;
          const current_remaining = Math.max(0, Math.floor((timerData.server_end_time - current_server_time) / 1000));
          
          // Add additional seconds to the server_end_time
          const new_server_end_time = timerData.server_end_time + (additionalSeconds * 1000);
          
          localStorage.setItem("turnTimer", JSON.stringify({ 
            ...timerData,
            server_end_time: new_server_end_time
          }));
          
          // Update timer seconds
          const new_remaining = current_remaining + additionalSeconds;
          setTimerSeconds(new_remaining);
          
          // If paused, also update paused remaining
          if (isPaused && savedPause) {
            try {
              const pauseData = JSON.parse(savedPause);
              if (typeof pauseData.remaining === "number") {
                const new_paused_remaining = pauseData.remaining + additionalSeconds;
                localStorage.setItem("turnTimerPaused", JSON.stringify({ 
                  ...pauseData,
                  remaining: new_paused_remaining,
                  server_end_time: new_server_end_time
                }));
                setPausedRemaining(new_paused_remaining);
              }
            } catch (e) {
              console.error("Error updating paused state for extend:", e);
            }
          }
        } else if (timerData.endTime) {
          // Old format: backward compatibility
          const current_remaining = Math.max(0, Math.floor((timerData.endTime - Date.now()) / 1000));
          const new_endTime = timerData.endTime + (additionalSeconds * 1000);
          localStorage.setItem("turnTimer", JSON.stringify({ endTime: new_endTime }));
          const new_remaining = current_remaining + additionalSeconds;
          setTimerSeconds(new_remaining);
          
          if (isPaused && savedPause) {
            try {
              const pauseData = JSON.parse(savedPause);
              if (typeof pauseData.remaining === "number") {
                const new_paused_remaining = pauseData.remaining + additionalSeconds;
                localStorage.setItem("turnTimerPaused", JSON.stringify({ 
                  paused: true, 
                  remaining: new_paused_remaining
                }));
                setPausedRemaining(new_paused_remaining);
              }
            } catch (e) {
              console.error("Error updating paused state for extend:", e);
            }
          }
        }
      } catch (e) {
        console.error("Error parsing turnTimer for extend:", e);
      }
    } else if (savedPause) {
      // Timer is paused but no active timer - extend the paused remaining
      try {
        const pauseData = JSON.parse(savedPause);
        if (typeof pauseData.remaining === "number") {
          const new_remaining = pauseData.remaining + additionalSeconds;
          localStorage.setItem("turnTimerPaused", JSON.stringify({ 
            ...pauseData,
            remaining: new_remaining
          }));
          setPausedRemaining(new_remaining);
          setTimerSeconds(new_remaining);
        }
      } catch (e) {
        console.error("Error parsing turnTimerPaused for extend:", e);
      }
    }
  };

  return (
    <TimerContext.Provider value={{ timerSeconds, isPaused, startTimer, stopTimer, pauseTimer, resumeTimer, extendTimer }}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer() {
  const context = useContext(TimerContext);
  if (context === undefined) {
    throw new Error("useTimer must be used within a TimerProvider");
  }
  return context;
}

